import type { Person } from '@ambit/shared';
import { AddPerson } from './Memory.tsx';

/**
 * The household roster, and the only way into it.
 *
 * Rendered even when empty, which is the whole point. Written as
 * `people.length > 0 && (...)` this section disappeared entirely at zero — no
 * heading, no prompt, no control — and `POST /api/people` had existed the whole
 * time with nothing in the UI calling it. The result was a catalog of 250
 * researched outings with nobody in the household to vote on any of them.
 *
 * Hidden-when-empty is a reasonable default for a list of things you have. It
 * is the wrong default for the one state that most needs a prompt.
 *
 * Split out of App so the empty branch can actually be rendered in a test. The
 * first attempt to verify this ran a script against the dev database, which had
 * four people in it, and so exercised the populated path twice.
 */
export function Household({
  people,
  me,
  needsIdentity,
  onPick,
  onAdded,
}: {
  people: Person[];
  me: string | null;
  needsIdentity: boolean;
  onPick: (id: string) => void;
  onAdded: (person: Person) => void;
}) {
  return (
    <section aria-labelledby="who-heading" className="mt-12 border-t border-spruce-800 pt-6">
      <h2 id="who-heading" className="display text-xs uppercase tracking-widest text-bone-500">
        Who are you?
      </h2>
      {needsIdentity && !me && (
        <p role="alert" className="mt-1 text-sm text-blaze-300">
          Pick yourself first, then you can vote.
        </p>
      )}
      {people.length === 0 && (
        <p className="mt-1 text-sm text-bone-500">
          Add everyone who comes along. Voting on a day and recording who was there both need
          names.
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {people.map((person) => (
          <button
            key={person.id}
            type="button"
            aria-pressed={me === person.id}
            onClick={() => onPick(person.id)}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              me === person.id
                ? 'bg-blaze-500 font-semibold text-spruce-950'
                : 'bg-spruce-800 text-bone-300 hover:bg-spruce-700'
            }`}
          >
            {person.name}
          </button>
        ))}
        <AddPerson onAdded={onAdded} />
      </div>
    </section>
  );
}
